import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { Eye, Users, TrendingUp, Calendar } from "lucide-react";

const Card = ({ icon: Icon, label, value, sub }) => (
  <div className="border border-[#e5e1d8] bg-[#f9f8f6] p-5 sm:p-6">
    <div className="flex items-center gap-2 mb-3 text-[#c5a873]">
      <Icon size={16} />
      <span className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#4a4843]">{label}</span>
    </div>
    <p className="font-serif text-3xl sm:text-4xl text-[#1a1918] tracking-tight">{value.toLocaleString("it-IT")}</p>
    {sub && <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#4a4843]/70 mt-2">{sub}</p>}
  </div>
);

const AdminStats = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/stats/visits");
        setData(r.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Build a 30-day series filling missing days with 0
  const series = (() => {
    if (!data?.daily) return [];
    const map = new Map(data.daily.map((d) => [d.day, d]));
    const out = [];
    const today = new Date();
    for (let i = 29; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      out.push({ day: key, views: map.get(key)?.views || 0, uv: map.get(key)?.uv || 0 });
    }
    return out;
  })();

  const maxV = Math.max(1, ...series.map((s) => s.views));

  return (
    <AdminShell title="Statistiche">
      <div className="max-w-5xl">
        <p className="font-serif italic text-base sm:text-lg text-[#4a4843] mb-8 leading-relaxed">
          Conteggi anonimi delle visite al sito pubblico. Nessun cookie, nessun servizio terzo: i visitatori sono identificati da un hash di IP+browser, non riconducibile alla persona.
        </p>

        {loading ? (
          <p className="font-sans text-sm text-[#4a4843]">caricamento…</p>
        ) : !data ? (
          <p className="font-serif italic text-[#4a4843]">nessun dato.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-10">
              <Card icon={Eye} label="Visite oggi" value={data.today_views} sub={`${data.uv_today} visitatori unici`} />
              <Card icon={TrendingUp} label="Visite (7gg)" value={data.week_views} sub={`${data.uv_week} unici`} />
              <Card icon={Users} label="Visite totali" value={data.total_views} sub={`${data.uv_total} unici`} />
            </div>

            <div className="border border-[#e5e1d8] bg-[#f9f8f6] p-5 sm:p-6 mb-10">
              <div className="flex items-center gap-2 mb-4 text-[#c5a873]">
                <Calendar size={16} />
                <span className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#4a4843]">Ultimi 30 giorni</span>
              </div>
              <div className="flex items-end gap-1 h-32" data-testid="stats-chart">
                {series.map((s) => (
                  <div key={s.day} className="group flex-1 flex flex-col items-center justify-end h-full" title={`${s.day}: ${s.views} visite, ${s.uv} unici`}>
                    <div
                      className="w-full bg-[#1a1918] hover:bg-[#c5a873] transition-colors"
                      style={{ height: `${(s.views / maxV) * 100}%`, minHeight: s.views > 0 ? "2px" : "0" }}
                    />
                  </div>
                ))}
              </div>
              <div className="flex justify-between font-sans text-[9px] tracking-[0.2em] uppercase text-[#4a4843]/70 mt-2">
                <span>{series[0]?.day.slice(5)}</span>
                <span>oggi</span>
              </div>
            </div>

            <div>
              <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#4a4843] mb-4">
                Post più letti
              </p>
              {data.top_posts.length === 0 ? (
                <p className="font-serif italic text-[#4a4843] py-8 text-center border border-dashed border-[#e5e1d8]">
                  Ancora nessuna lettura tracciata.
                </p>
              ) : (
                <ul className="divide-y divide-[#e5e1d8] border-t border-b border-[#e5e1d8]">
                  {data.top_posts.map((p, i) => (
                    <li key={p.post_id} className="flex items-center gap-4 py-3" data-testid={`top-post-${p.post_id}`}>
                      <span className="font-sans text-xs tracking-[0.2em] uppercase text-[#c5a873] w-8">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-serif text-lg text-[#1a1918] truncate">{p.title}</p>
                        {p.author && <p className="font-serif italic text-sm text-[#4a4843]">di {p.author}</p>}
                      </div>
                      <span className="font-sans text-[10px] tracking-[0.25em] uppercase bg-[#1a1918] text-[#f9f8f6] px-2.5 py-1">
                        {p.views} {p.views === 1 ? "lettura" : "letture"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
    </AdminShell>
  );
};

export default AdminStats;
